import React, { useState } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const AddUser = () => {
  const navigate = useNavigate();
  const initialFormState = {
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    role: "user",
    unit: ""
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // Clear error when user types
    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: ""
      }));
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }
    if (formData.role === "user" && !formData.unit.trim()) {
      newErrors.unit = "Unit is required for user role";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Prepare the data to send to API
      const apiData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        role: formData.role,
        unit: formData.unit // Include unit parameter
      };

      const response = await axios.post('https://usibras-web-backend.onrender.com/api/v1/auth/register', apiData);
      
      if (response.data.message === "User registered successfully") {
        showAlert('User added successfully!', 'success');
        setFormData(initialFormState);
        setTimeout(() => navigate('/admin/users'), 1500);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      showAlert(error.response?.data?.message || 'Failed to add user', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-3 h-full">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-navy-700 font-medium">Processing...</p>
          </div>
        </div>
      )}

      {/* Alert */}
      {alert.show && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
          alert.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white z-50`}>
          {alert.message}
        </div>
      )}

      <Card extra={"w-full p-4"}>
        <div className="mb-8 w-full">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Add New User
          </h4>
          <p className="mt-2 text-base text-gray-600">
            Please fill in all the required information
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              User Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="First Name*"
                id="first_name"
                type="text"
                placeholder="Enter first name"
                value={formData.first_name}
                onChange={handleChange}
                error={errors.first_name}
                required
              />

              <InputField
                label="Last Name*"
                id="last_name"
                type="text"
                placeholder="Enter last name"
                value={formData.last_name}
                onChange={handleChange}
                error={errors.last_name}
                required
              />

              <InputField
                label="Username*"
                id="username"
                type="text"
                placeholder="Enter username"
                value={formData.username}
                onChange={handleChange}
                error={errors.username}
                required
              />

              <InputField
                label="Email*"
                id="email"
                type="email"
                placeholder="mail@example.com"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
              />

              <InputField
                label="Phone"
                id="phone"
                type="tel"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={handleChange}
              />

              <InputField
                label="Password*"
                id="password"
                type="password"
                placeholder="Min. 6 characters"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                required
              />

              <div>
                <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                  Role*
                </label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none border-gray-200 dark:!border-white/10 dark:text-white"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Units dropdown - only show when role is "user" */}
              {formData.role === "user" && (
                <div>
                  <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                    Unit*
                  </label>
                  <select
                    id="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none border-gray-200 dark:!border-white/10 dark:text-white"
                  >
                    <option value="">Select a unit</option>
                    <option value="Dispatch RCN">Dispatch RCN</option>
                    <option value="RCN Warehouse">RCN Warehouse</option>
                    <option value="Finish Pro Warehouse">Finish Pro Warehouse</option>
                    <option value="Analyst">Analyst</option>
                    <option value="Manual Peeling">Manual Peeling</option>
                    <option value="Field Analyst">Field Analyst</option>
                    <option value="Factory Analyst">Factory Analyst</option>
                  </select>
                  {errors.unit && (
                    <p className="mt-1 text-sm text-red-500">{errors.unit}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => setFormData(initialFormState)}
              className="rounded-xl bg-gray-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-gray-600 active:bg-gray-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900"
              disabled={isLoading}
            >
              Clear
            </button>
            <button
              type="submit"
              className="rounded-xl bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900 flex items-center justify-center min-w-[120px]"
              disabled={isLoading}
            >
              Submit
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddUser; 